import { Session, Message, Scenario, CoachTurn } from "../types";
import { API_BASE_URL } from "../constants";

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

export async function getSessions(token?: string): Promise<Session[]> {
  const response = await fetch(`${API_BASE_URL}/sessions`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch sessions: ${await response.text()}`);
  }
  return response.json();
}

export async function createSession(
  scenarioId: string,
  token?: string,
): Promise<Session> {
  const response = await fetch(`${API_BASE_URL}/sessions`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ scenarioId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to create session: ${await response.text()}`);
  }
  return response.json();
}

export async function getSessionMessages(
  sessionId: string,
  token?: string,
): Promise<Message[]> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/messages`,
    { headers: authHeaders(token) },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch messages: ${await response.text()}`);
  }
  return response.json();
}

export async function updateSession(
  sessionId: string,
  data: { name?: string | null },
  token?: string,
): Promise<Session> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
    method: "PATCH",
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Failed to update session: ${await response.text()}`);
  }
  return response.json();
}

export async function deleteSession(
  sessionId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to delete session: ${await response.text()}`);
  }
}

export async function getScenario(
  id: string,
  token?: string,
): Promise<Scenario> {
  const response = await fetch(`${API_BASE_URL}/scenarios/${id}`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch scenario: ${await response.text()}`);
  }
  return response.json();
}

export async function getScenarioLibrary(
  token?: string,
  // No createdByUserId: ownership is answered by the server-computed
  // `canEdit`, and rubricId/rubricName are absent entirely for members.
): Promise<(Pick<Scenario, 'id' | 'title' | 'description' | 'category'> & { canEdit: boolean; rubricId?: string | null; rubricName?: string | null; hero?: string })[]> {
  const response = await fetch(`${API_BASE_URL}/scenarios`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch scenario library: ${await response.text()}`);
  }
  return response.json();
}

export async function postMessage(
  sessionId: string,
  message: string,
  personaId: string,
  token?: string,
): Promise<{ text: string; revealedDocId?: string }> {
  const response = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/message`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ message, personaId }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to send message: ${await response.text()}`);
  }
  return response.json();
}

export async function getCoachHistory(
  sessionId: string,
  token?: string,
): Promise<{ turns: CoachTurn[] }> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/coach`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch coach history: ${await response.text()}`);
  }
  return response.json();
}

export async function postCoachMessage(
  sessionId: string,
  message: string,
  personaId: string,
  scratchpad: string | undefined,
  token?: string,
): Promise<{ text: string }> {
  const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/coach`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ message, personaId, scratchpad }),
  });
  if (!response.ok) {
    throw new Error(`Failed to send coach message: ${await response.text()}`);
  }
  return response.json();
}

export async function getAudioStatus(
  token?: string,
): Promise<{ ttsEnabled: boolean }> {
  const response = await fetch(`${API_BASE_URL}/audio/status`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch audio status: ${await response.text()}`);
  }
  return response.json();
}

export async function synthesizeSpeech(
  text: string,
  personaId: string,
  token?: string,
): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/audio/tts`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ text, personaId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to synthesize speech: ${await response.text()}`);
  }
  return response.blob();
}
